import { useCallback } from "react"

import { ROUTE_PERMISSION_REQUIREMENTS } from "#constants/routePermissions"
import { useAuthorizationContext } from "#contexts/AuthorizationContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  Resource,
  RESOURCES,
  USER_ACTIONS,
  UserAction,
} from "#customTypes/composedTypes/authTypes"
import { hasPermission } from "#utils/permissionsHelpers"

const useHasPermission = (resource: Resource, action: UserAction) => {
  const { effectiveRole, isPinLoggedIn, effectiveUserId } = useUserContext()
  const { isManagerLoggedIn } = useAuthorizationContext()

  if (!isManagerLoggedIn || !isPinLoggedIn || !effectiveUserId) return false

  return hasPermission(effectiveRole, resource, action)
}

export const useResourcePermissions = (resource: Resource) => {
  const canCreate = useHasPermission(resource, USER_ACTIONS.CREATE)
  const canRead = useHasPermission(resource, USER_ACTIONS.READ)
  const canUpdate = useHasPermission(resource, USER_ACTIONS.UPDATE)
  const canDelete = useHasPermission(resource, USER_ACTIONS.DELETE)

  return {
    canCreate,
    canRead,
    canUpdate,
    canDelete,
  }
}

export const useCanSeeIds = () => {
  return useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)
}

export const useRouteAccessChecker = () => {
  const { effectiveRole, isPinLoggedIn, effectiveUserId } = useUserContext()
  const { isManagerLoggedIn } = useAuthorizationContext()

  return useCallback(
    (route: string) => {
      if (!isManagerLoggedIn || !isPinLoggedIn || !effectiveUserId) {
        return false
      }

      const requirement = ROUTE_PERMISSION_REQUIREMENTS[route]
      if (!requirement) return true

      return hasPermission(
        effectiveRole,
        requirement.resource,
        requirement.action
      )
    },
    [effectiveRole, effectiveUserId, isManagerLoggedIn, isPinLoggedIn]
  )
}

export const useCanAccessRoute = (route: string) => {
  const canAccessRoute = useRouteAccessChecker()
  return canAccessRoute(route)
}

export default useHasPermission
